import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BookOpen, Printer, ChevronLeft, ChevronRight, Compass } from 'lucide-react'
import { Logo } from '../components/Logo'
import { useAuthStore } from '../stores/authStore'
import { manualForUser } from '../lib/user-manual'
import { startProductTour } from '../components/ProductTour/useProductTour'
import { Breadcrumb } from '../components/Breadcrumb'

// ── Search highlight ──────────────────────────────────────────────
// Wrap case-insensitive matches of `q` in <mark>. Returns the raw string
// when there's no active query (so non-searched text renders untouched).
function highlight(text, q) {
  if (!q) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1) return text
  const out = []
  let i = 0
  let from = idx
  while (from !== -1) {
    if (from > i) out.push(text.slice(i, from))
    out.push(
      <mark
        key={i}
        className="rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] px-0.5"
      >
        {text.slice(from, from + q.length)}
      </mark>,
    )
    i = from + q.length
    from = text.toLowerCase().indexOf(q.toLowerCase(), i)
  }
  if (i < text.length) out.push(text.slice(i))
  return out
}

// ── Block renderer ────────────────────────────────────────────────
function Block({ block, q }) {
  if (typeof block === 'string') {
    return (
      <p className="text-sm leading-relaxed text-[color:var(--color-ink)] mb-3">
        {highlight(block, q)}
      </p>
    )
  }
  if (block.sub) {
    return (
      <h4 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mt-5 mb-2">
        {highlight(block.sub, q)}
      </h4>
    )
  }
  if (block.note) {
    return (
      <div className="mb-3 rounded-md border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-3 py-2 text-sm leading-relaxed text-[color:var(--color-ink)]">
        {highlight(block.note, q)}
      </div>
    )
  }
  if (block.verify) {
    return (
      <div className="mb-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm leading-relaxed text-[color:var(--color-ink-muted)]">
        <span className="font-semibold text-[color:var(--color-ink)]">
          Needs verification:{' '}
        </span>
        {highlight(block.verify, q)}
      </div>
    )
  }
  if (block.list) {
    return (
      <ul className="mb-3 ml-4 list-disc space-y-1.5 text-sm leading-relaxed text-[color:var(--color-ink)] marker:text-[color:var(--color-ink-subtle)]">
        {block.list.map((li, i) => (
          <li key={i}>{highlight(li, q)}</li>
        ))}
      </ul>
    )
  }
  if (block.steps) {
    return (
      <ol className="mb-3 ml-5 list-decimal space-y-1.5 text-sm leading-relaxed text-[color:var(--color-ink)] marker:text-[color:var(--color-ink-subtle)]">
        {block.steps.map((li, i) => (
          <li key={i}>{highlight(li, q)}</li>
        ))}
      </ol>
    )
  }
  if (block.image) {
    // Screenshot / illustration. `image` is a URL or app-root path (e.g.
    // '/manual/dashboard.png' served from public/manual/); optional `caption`
    // renders below it and doubles as the alt text when `alt` isn't supplied.
    // A thin token border frames it cleanly in both light and dark mode; the
    // image is fully responsive (never overflows the manual column).
    const alt = block.alt ?? block.caption ?? 'Screenshot'
    // Screenshots read wider than the body text: the figure spills ~28px each
    // side into the page's inner padding so the image is noticeably larger than
    // the column, still framed with a comfortable edge margin. The measurer
    // renders the same figure, so pagination stays in sync.
    return (
      <figure className="mb-4 -mx-7">
        {/* Hover magnifier: moving the cursor over the screenshot zooms into
         *  that exact spot — the image scales inside its clipped frame and pans
         *  with the pointer — so fine detail is legible without a separate zoom
         *  control. Leaving the image resets it. */}
        <div
          className="um-zoom overflow-hidden rounded-lg border border-[color:var(--color-rule)] shadow-sm"
          onMouseMove={(e) => {
            const img = e.currentTarget.querySelector('img')
            if (!img) return
            const r = e.currentTarget.getBoundingClientRect()
            const x = ((e.clientX - r.left) / r.width) * 100
            const y = ((e.clientY - r.top) / r.height) * 100
            img.style.transformOrigin = `${x}% ${y}%`
            img.style.transform = 'scale(2.2)'
          }}
          onMouseLeave={(e) => {
            const img = e.currentTarget.querySelector('img')
            if (img) {
              img.style.transform = ''
              img.style.transformOrigin = ''
            }
          }}
        >
          <img
            src={block.image}
            alt={alt}
            // Eager, NOT lazy: the off-screen measurer (left:-99999px) that
            // drives pagination would never load a lazy image, so it would
            // measure the figure as ~0px tall and over-pack the page — then the
            // real, loaded image overflows. Loading eagerly makes the measured
            // height match what actually renders.
            className="um-zoom__img block w-full h-auto max-w-full sm:max-h-[560px]"
          />
        </div>
        {block.caption ? (
          <figcaption className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]">
            {highlight(block.caption, q)}
          </figcaption>
        ) : null}
      </figure>
    )
  }
  if (block.table) {
    return (
      <div className="mb-3 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {block.table.head.map((h, i) => (
                <th
                  key={i}
                  className="border-b border-[color:var(--color-rule)] px-3 py-2 text-left font-semibold text-[color:var(--color-ink-strong)] align-top"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.table.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="border-b border-[color:var(--color-rule)] px-3 py-2 align-top text-[color:var(--color-ink)]"
                  >
                    {highlight(String(cell), q)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  return null
}

// ── Print / Save as PDF ───────────────────────────────────────────
// The manual renders deep inside the app's fixed-header + overflow-auto
// layout, where the old `body * { visibility: hidden }` trick clipped the
// output to one blank page. Instead we serialise the manual to a clean,
// self-contained HTML document and print THAT in a new window — reliable
// across browsers, styled for print, and always the FULL guide regardless
// of the on-screen search filter.
// Quotes are escaped too: `esc` is also used inside double-quoted ATTRIBUTES
// (<img src="…" alt="…">), where a bare quote would end the attribute.
const esc = (s) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch],
  )

function blockToPrintHtml(block) {
  if (typeof block === 'string') return `<p>${esc(block)}</p>`
  if (!block || typeof block !== 'object') return ''
  if (block.sub) return `<h3>${esc(block.sub)}</h3>`
  if (block.note) return `<div class="note">${esc(block.note)}</div>`
  if (block.verify)
    return `<div class="verify"><b>Needs verification: </b>${esc(block.verify)}</div>`
  if (block.list)
    return `<ul>${block.list.map((li) => `<li>${esc(li)}</li>`).join('')}</ul>`
  if (block.steps)
    return `<ol>${block.steps.map((li) => `<li>${esc(li)}</li>`).join('')}</ol>`
  if (block.table) {
    const head = (block.table.head ?? [])
      .map((h) => `<th>${esc(h)}</th>`)
      .join('')
    const rows = (block.table.rows ?? [])
      .map(
        (r) => `<tr>${r.map((c) => `<td>${esc(String(c))}</td>`).join('')}</tr>`,
      )
      .join('')
    return `<table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`
  }
  if (block.image) {
    const alt = esc(block.alt ?? block.caption ?? 'Screenshot')
    const cap = block.caption
      ? `<figcaption>${esc(block.caption)}</figcaption>`
      : ''
    return `<figure class="img"><img src="${esc(block.image)}" alt="${alt}"/>${cap}</figure>`
  }
  return ''
}

const PRINT_DOC_CSS = `
  *{box-sizing:border-box}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#0f172a;line-height:1.55;font-size:13px;max-width:820px;margin:0 auto;padding:28px 24px}
  h1{font-size:24px;margin:0 0 4px}
  p.sub{color:#64748b;margin:0 0 20px}
  section{break-inside:avoid;margin:0 0 20px}
  h2{font-size:17px;border-bottom:1px solid #e5e7eb;padding-bottom:6px;margin:22px 0 10px}
  h3{font-size:13px;margin:16px 0 6px}
  p{margin:0 0 10px}
  ul,ol{margin:0 0 10px;padding-left:20px}
  li{margin:0 0 5px}
  table{width:100%;border-collapse:collapse;margin:0 0 12px;font-size:12px}
  th,td{border-bottom:1px solid #e5e7eb;padding:6px 8px;text-align:left;vertical-align:top}
  th{color:#0f172a}
  .note{border:1px solid #c7d2fe;background:#eef2ff;border-radius:6px;padding:8px 10px;margin:0 0 10px}
  .verify{border:1px solid #e5e7eb;background:#f8fafc;border-radius:6px;padding:8px 10px;margin:0 0 10px;color:#475569}
  figure.img{margin:0 0 12px;break-inside:avoid}
  figure.img img{max-width:640px;max-height:480px;height:auto;width:auto;border:1px solid #e5e7eb;border-radius:6px}
  figure.img figcaption{color:#64748b;font-size:11px;margin-top:4px}
  @page{margin:16mm}
`

/** Open the manual as a clean standalone document and print it. */
function printManual(manual) {
  const body = manual.sections
    .map(
      (s) =>
        `<section><h2>${esc(s.title)}</h2>${s.blocks.map(blockToPrintHtml).join('')}</section>`,
    )
    .join('')
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(
    manual.title,
  )}</title><style>${PRINT_DOC_CSS}</style></head><body><h1>${esc(
    manual.title,
  )}</h1><p class="sub">${esc(manual.subtitle)}</p>${body}</body></html>`

  // NB: do NOT pass 'noopener' — per spec that makes window.open return null,
  // which would strand a blank tab and wrongly trigger the fallback below.
  const win = window.open('', '_blank')
  if (!win) {
    // Popup genuinely blocked — fall back to the browser's own print.
    window.print()
    return
  }
  win.document.open()
  win.document.write(html)
  win.document.close()
  win.focus()
  win.onafterprint = () => win.close()
  // Content is fully inline (no images/fonts to await), so a short tick is
  // enough for the new document to lay out before the dialog opens.
  setTimeout(() => {
    try {
      win.print()
    } catch {
      /* user closed the window before it printed */
    }
  }, 150)
}

// A4 geometry + pagination constants. `A4_W_PX` is 210 mm at 96 dpi; the JS
// padding here MUST match `.um-paper__body` in BOOK_CSS so the off-screen
// measurer's content width equals the real page's, and the height budget lines
// up. FLIP_MS must match the CSS `--flip` duration.
const A4_RATIO = 297 / 210
const A4_W_PX = 793.7
// Floor the page width so a very narrow phone can't shrink the sheet so far
// that a single atomic block (e.g. a long paragraph) no longer fits one page.
// Below this the sheet stays put and the book AREA scrolls — never the page
// body — per the responsive rule.
// Floored wide enough that even the tallest single block (a long list /
// paragraph) fits one A4 page at the resulting height — below this a block can
// overflow and clip. The whole sheet then scales to fit the viewport.
const MIN_PAGE_W = 540
// Two-page (open-book) spread kicks in once the viewport is at least this wide;
// below it the book stays single-page (turned with the existing flip).
const TWO_UP_MIN = 820
const SPREAD_GAP = 0 // px between the two pages (they meet at the spine)
const PAD = { top: 40, right: 44, bottom: 40, left: 48 } // px, == .um-paper__body
const FLIP_MS = 780
// Vertical space below the sheet reserved for the thin page-counter bar,
// subtracted from the viewport when fitting to height. Kept small so the book
// grows down into the space the removed buttons used to take.
const BOOK_CHROME_RESERVE = 30
// Shared by the real page heading AND the measurer so measured heights match.
const PAGE_HEADING_CLASS =
  'font-display mb-4 border-b border-black/10 pb-2 text-xl font-semibold tracking-tight text-[color:var(--color-ink-strong)] dark:border-white/10'

// Book skin + realistic 3D page-turn. Self-contained (no external CSS file).
// The turning leaf is a double-sided sheet rotated around the spine
// (transform-origin: left) with preserve-3d + backface-visibility so the
// content front and the paper back render correctly; the destination page sits
// underneath and is revealed as the leaf turns. Content is PAGINATED to fit an
// A4 sheet (see the measurer), so a page never scrolls. Respects
// prefers-reduced-motion (falls back to a plain cross-fade).
const BOOK_CSS = `
.um-stage {
  --flip: ${FLIP_MS}ms;
  position: relative;
  width: 210mm;
  max-width: 100%;
  aspect-ratio: 210 / 297;
  margin-inline: auto;
  perspective: 2000px;
}
.um-sheet { position: absolute; inset: 0; z-index: 1; }
/* Stacked-pages base so the sheet reads as the top of a real book (reference
 *  skin) — a few offset layers peeking out along the fore-edge + foot. */
.um-stage::before {
  content: '';
  position: absolute; inset: 0;
  border-radius: 2px 7px 7px 2px;
  background: #efe9df;
  box-shadow:
    3px 4px 0 #eae3d8,
    6px 8px 0 #e3dccf,
    9px 12px 0 #dcd4c6;
  z-index: 0;
}
/* The turning leaf — a double-sided sheet hinged on the spine (left edge). */
.um-turn {
  transform-style: preserve-3d;
  transform-origin: left center;
  will-change: transform;
  z-index: 2;
}
.um-turn--next { animation: um-turn-next var(--flip) ease-in-out both; }
.um-turn--prev { animation: um-turn-prev var(--flip) ease-in-out both; }
@keyframes um-turn-next { from { transform: rotateY(0deg); }   to { transform: rotateY(-180deg); } }
@keyframes um-turn-prev { from { transform: rotateY(-180deg); } to { transform: rotateY(0deg); } }

/* ── Two-page spread (open book) ─────────────────────────────────────────── */
.um-spread { position: relative; perspective: 2600px; }
.um-slot { position: absolute; top: 0; height: 100%; }
/* Centre-hinged turning leaf for the spread: Next turns the RIGHT page left,
 *  Prev turns the LEFT page right — both hinged on the central spine. */
.um-leaf {
  position: absolute; top: 0; height: 100%;
  transform-style: preserve-3d; will-change: transform; z-index: 3;
}
.um-leaf--next { transform-origin: left center; animation: um-turn-next var(--flip) ease-in-out both; }
.um-leaf--prev { transform-origin: right center; animation: um-leaf-prev var(--flip) ease-in-out both; }
@keyframes um-leaf-prev { from { transform: rotateY(0deg); } to { transform: rotateY(180deg); } }
/* Left-hand page — mirror the skin so the binding sits on the RIGHT. */
.um-paper--left {
  border-radius: 7px 2px 2px 7px;
  background: linear-gradient(270deg, #efe8dc 0%, #fdfaf4 20%);
  box-shadow:
    inset 0 -1px 2px rgba(50,50,50,.10),
    inset 1px 0 1px rgba(150,150,150,.20),
    0 22px 46px -26px rgba(0,0,0,.5);
}
.um-paper--left::before {
  left: auto; right: 0; border-radius: 0 2px 2px 0;
  background: linear-gradient(270deg, rgba(0,0,0,.17), rgba(0,0,0,.02) 65%, rgba(0,0,0,0));
}
.um-paper--left .um-paper__body { padding: 40px 48px 40px 44px; }
.um-paper--left .um-page__folio { right: auto; left: 1.75rem; }
.dark .um-paper--left { background: linear-gradient(270deg, #101116 0%, #16171b 20%); }
.dark .um-paper--left::before { background: linear-gradient(270deg, rgba(0,0,0,.5), rgba(0,0,0,.08) 65%, rgba(0,0,0,0)); }

/* ── Book covers (the closed book) ─────────────────────────────────────────── */
.um-cover {
  position: absolute; inset: 0;
  display: grid; place-items: center; text-align: center;
  border-radius: 2px 7px 7px 2px;
  background: radial-gradient(120% 100% at 50% 0%, #12325f 0%, #0a1f42 58%, #061631 100%);
  color: #f4eddc;
  border: 1px solid rgba(255,255,255,.10);
  box-shadow: inset 0 0 70px rgba(0,0,0,.35), 0 22px 46px -26px rgba(0,0,0,.6);
  cursor: pointer;
  overflow: hidden;
}
.um-cover::before {           /* spine band on the left */
  content: '';
  position: absolute; left: 0; top: 0; bottom: 0; width: 20px;
  background: linear-gradient(90deg, rgba(0,0,0,.5), rgba(0,0,0,0));
}
.um-cover__inner {
  display: grid; place-items: center;
  padding: 10% 12%;
}
.um-cover__logo { width: 62%; max-width: 420px; height: auto; }
/* Open/close of a cover — a gentle scale-in of the revealed view. */
.um-open { animation: um-open var(--flip, 780ms) ease both; }
@keyframes um-open { from { opacity: 0; transform: scale(.965); } to { opacity: 1; transform: scale(1); } }

.um-face { position: absolute; inset: 0; backface-visibility: hidden; }
.um-face--back {
  transform: rotateY(180deg);
  border-radius: 7px 2px 2px 7px;
  background: #f4efe1;
  border: 1px solid rgba(0,0,0,.10);
  box-shadow: inset 0 0 55px rgba(0,0,0,.05);
}
/* Depth shade on the turning leaf — darkens as it lifts (next) / lightens as it
 *  lands (prev), giving the turn physical depth. */
.um-face--front::after {
  content: '';
  position: absolute; inset: 0;
  border-radius: inherit;
  background: linear-gradient(to left, rgba(0,0,0,.4), rgba(0,0,0,0) 58%);
  pointer-events: none;
}
.um-turn--next .um-face--front::after,
.um-leaf--next .um-face--front::after { animation: um-shade-in var(--flip) ease both; }
.um-turn--prev .um-face--front::after,
.um-leaf--prev .um-face--front::after { animation: um-shade-out var(--flip) ease both; }
@keyframes um-shade-in  { from { opacity: 0; } to { opacity: .9; } }
@keyframes um-shade-out { from { opacity: .9; } to { opacity: 0; } }

/* Paper surface (idle sheet, static sheet, turning front face). */
.um-paper {
  position: absolute; inset: 0;
  border-radius: 2px 7px 7px 2px;
  /* Reference paper: a subtle spine-dark → fore-edge-light gradient with inset
   *  depth shadows, giving each leaf a gentle page curve. */
  background: linear-gradient(90deg, #efe8dc 0%, #fdfaf4 20%);
  border: 1px solid rgba(0,0,0,.12);
  box-shadow:
    inset 0 -1px 2px rgba(50,50,50,.10),
    inset -1px 0 1px rgba(150,150,150,.20),
    0 22px 46px -26px rgba(0,0,0,.5);
  overflow: hidden;
  backface-visibility: hidden;
}
.um-paper::before {           /* spine shadow */
  content: '';
  position: absolute; left: 0; top: 0; bottom: 0; width: 16px;
  border-radius: 2px 0 0 2px;
  background: linear-gradient(90deg, rgba(0,0,0,.17), rgba(0,0,0,.02) 65%, rgba(0,0,0,0));
  z-index: 1; pointer-events: none;
}
.um-paper__body {
  position: absolute; inset: 0;
  padding: 40px 44px 40px 48px;   /* == PAD; also the measurer's width budget */
  overflow: hidden;               /* content is paginated to fit — never scrolls */
}
/* Each page's first block sits flush at the top. The pagination measures a
 *  block by its border box (excluding its top margin), so without this the real
 *  padded page is that top margin TALLER than measured — pushing the last line
 *  under the folio / clipping it. Zeroing it makes rendered == measured. */
.um-paper__body > *:first-child,
.um-paper__body > *:first-child > :first-child { margin-top: 0 !important; }
.um-page__folio {
  position: absolute; bottom: .9rem; right: 1.75rem;
  font-size: 12px; letter-spacing: .05em; color: rgba(0,0,0,.42);
  z-index: 1;
}
/* Hover-to-zoom screenshot: the frame clips, the image scales into the hovered
 *  point. The zoom-in cursor hints the interaction; the transition eases it. */
.um-zoom { cursor: zoom-in; }
.um-zoom__img {
  display: block;
  transform: scale(1);
  transform-origin: center;
  transition: transform .18s ease;
  will-change: transform;
}
/* Off-screen measurer — never shown, only laid out to measure block heights. */
.um-measure { box-sizing: border-box; }
.um-measure *, .um-paper__body * { box-sizing: border-box; }
/* Reading text +1px for legibility — applied to the real page AND the measurer
 *  so pagination measures the same size the reader sees. */
.um-paper__body .text-sm, .um-measure .text-sm { font-size: 15px; }
.um-paper__body .text-xs, .um-measure .text-xs { font-size: 13px; }

.dark .um-paper {
  background: linear-gradient(90deg, #101116 0%, #16171b 20%);
  border-color: rgba(255,255,255,.10);
  box-shadow:
    inset 0 -1px 2px rgba(0,0,0,.4),
    inset -1px 0 1px rgba(0,0,0,.35),
    0 22px 46px -26px rgba(0,0,0,.75);
}
.dark .um-paper::before { background: linear-gradient(90deg, rgba(0,0,0,.5), rgba(0,0,0,.08) 65%, rgba(0,0,0,0)); }
.dark .um-stage::before {
  background: #14151a;
  box-shadow: 3px 4px 0 #101116, 6px 8px 0 #0d0e12, 9px 12px 0 #0a0b0e;
}
.dark .um-face--back { background: #121317; border-color: rgba(255,255,255,.08); }
.dark .um-page__folio { color: rgba(255,255,255,.42); }

@media (prefers-reduced-motion: reduce) {
  .um-turn--next, .um-turn--prev,
  .um-leaf--next, .um-leaf--prev { animation: um-fade 200ms ease both; }
  .um-face--back, .um-face--front::after { display: none; }
  @keyframes um-fade { from { opacity: 0; } to { opacity: 1; } }
}
`

export function UserManualPage() {
  const user = useAuthStore((s) => s.user)
  const { pathname } = useLocation()
  // Shared by the /app and /super-admin routes — point the breadcrumb home
  // at whichever dashboard the current surface belongs to.
  const isSuperAdmin = pathname.startsWith('/super-admin')
  const homePath = isSuperAdmin ? '/super-admin/dashboard' : '/dashboard'
  const manual = useMemo(() => manualForUser(user), [user])
  // Search was removed by request — the book is navigated with Previous / Next
  // (and the ← / → keys) only. Dropping the search row reclaims that vertical
  // space so the A4 page scales up and reads larger. `q` stays '' so the block
  // highlighter is a no-op and every section is shown.
  const q = ''
  const visibleSections = manual.sections

  // ── A4 pagination ────────────────────────────────────────────────────────
  // Track the available width so the A4 sheet (and its content budget) scale
  // down on narrow viewports rather than adding a scrollbar.
  const frameRef = useRef(null)
  const [avail, setAvail] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = frameRef.current
    if (!el) return undefined
    const measure = () => {
      const rect = el.getBoundingClientRect()
      // Available height = from the top of the book area down to the viewport
      // bottom, minus the Previous/Next controls + a small margin — so the WHOLE
      // A4 page fits vertically without the page (or app) scrolling.
      const h = Math.max(
        window.innerHeight - rect.top - BOOK_CHROME_RESERVE,
        220,
      )
      setAvail({ w: el.clientWidth, h })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  // Two pages side-by-side (open book) on wide viewports; single page below.
  const twoUp = (avail.w || 0) >= TWO_UP_MIN
  // Per-page layout width. Two-up: each page takes ~half the width so the spread
  // spans the viewport. Single: up to a full A4.
  const pageW = twoUp
    ? Math.min(A4_W_PX, Math.max(Math.floor(((avail.w || A4_W_PX) - SPREAD_GAP) / 2), MIN_PAGE_W))
    : Math.min(A4_W_PX, Math.max(avail.w || A4_W_PX, MIN_PAGE_W))
  // True A4 proportion (210 × 297) for every page — a printable, professional
  // sheet. (An earlier experiment widened the pages to fill horizontal slack,
  // but that broke the A4 ratio and shortened the page, squeezing tall content;
  // a correctly-proportioned A4 sheet is the right trade.)
  const contentW = Math.max(Math.round(pageW - PAD.left - PAD.right), 120)
  const contentH = Math.max(Math.round(pageW * A4_RATIO - PAD.top - PAD.bottom), 120)
  const pageH = pageW * A4_RATIO
  const spreadW = twoUp ? pageW * 2 + SPREAD_GAP : pageW
  // Two-up: fit the WHOLE spread inside the viewport (both axes) so the open
  // book is fully visible. Single: fit to width (a big page that scrolls
  // vertically). Never scaled above 1.
  const fit = twoUp
    ? Math.min(1, (avail.w || spreadW) / spreadW, (avail.h || pageH) / pageH)
    : Math.min(1, (avail.w || pageW) / pageW)


  // Off-screen measurer renders every section's heading + blocks at the exact
  // content width; re-measures when late-loading images change its size.
  const measureRef = useRef(null)
  const [measureTick, setMeasureTick] = useState(0)
  useLayoutEffect(() => {
    const m = measureRef.current
    if (!m) return undefined
    const bump = () => setMeasureTick((t) => t + 1)
    const ro = new ResizeObserver(bump)
    ro.observe(m)
    // Re-pack when each measurer image finishes loading. The images are eager
    // but still land asynchronously, and a ResizeObserver on the off-screen,
    // visibility:hidden measurer can miss those height changes — so the packer
    // would keep the pre-load (0-height) pagination and the real images would
    // overflow. An explicit per-image load listener guarantees a re-pack.
    const imgs = [...m.querySelectorAll('img')]
    const onImg = () => bump()
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener('load', onImg, { once: true })
    })
    return () => {
      ro.disconnect()
      imgs.forEach((img) => img.removeEventListener('load', onImg))
    }
  }, [visibleSections])

  // Greedy packer: distribute EVERY section's heading + blocks across A4-height
  // pages as ONE continuous stream — so a section starts on the previous
  // section's page whenever there is room (no forced page break per section) and
  // the pages fill up instead of leaving gaps. A page therefore holds one or
  // more section "segments"; a heading (section or sub) is never orphaned at the
  // foot of a page.
  const [pages, setPages] = useState([])
  useLayoutEffect(() => {
    const m = measureRef.current
    if (!m) {
      setPages([])
      return
    }
    // Small safety margin below the content area. The folio sits in the page's
    // 40px bottom padding (already outside contentH), so only a few px are
    // needed here to absorb sub-pixel measurement drift — keeping this tight
    // lets a block that *just* fits stay on the page instead of jumping over and
    // leaving a large blank.
    const budget = contentH - 8

    // Flatten every section's measured items into one continuous stream, each
    // tagged with its section + the block it renders. offsetTop is continuous
    // across the whole measurer, so a single greedy pass packs across sections.
    const stream = []
    for (const s of visibleSections) {
      const secEl = m.querySelector(`[data-msec="${s.id}"]`)
      const els = secEl ? [...secEl.querySelectorAll('[data-mi]')] : []
      for (const el of els) {
        const isSectionHeading = el.dataset.mi === 'h'
        const bi = el.dataset.bi != null ? Number(el.dataset.bi) : null
        const block = isSectionHeading ? null : s.blocks[bi]
        stream.push({
          sectionId: s.id,
          sectionTitle: s.title,
          isSectionHeading,
          isSub:
            !isSectionHeading &&
            block &&
            typeof block === 'object' &&
            'sub' in block,
          block,
          top: el.offsetTop,
          bottom: el.offsetTop + el.offsetHeight,
        })
      }
    }

    const out = []
    // Group a page's items into consecutive section-segments; a segment carries
    // the section title only when the section heading actually sits on this page
    // (a section continued from the previous page repeats no heading).
    const flushPage = (items) => {
      if (items.length === 0) return
      const segments = []
      for (const it of items) {
        let seg = segments[segments.length - 1]
        if (!seg || seg.sectionId !== it.sectionId) {
          seg = { sectionId: it.sectionId, title: null, blocks: [] }
          segments.push(seg)
        }
        if (it.isSectionHeading) seg.title = it.sectionTitle
        else seg.blocks.push(it.block)
      }
      out.push({ id: `p-${out.length}`, segments })
    }

    let pageTop = stream.length ? stream[0].top : 0
    let pageItems = []
    for (const it of stream) {
      // "Real" content = neither a section heading nor a sub-heading. A page
      // holding only headings keeps accumulating (a heading and its first block
      // never split).
      const realCount = pageItems.filter(
        (x) => !x.isSectionHeading && !x.isSub,
      ).length
      const onlyHeadings = pageItems.length > 0 && realCount === 0
      if (pageItems.length > 0 && it.bottom - pageTop > budget && !onlyHeadings) {
        // Peel trailing heading(s) — section OR sub — so they lead the next page
        // with the block that triggered the break, while this page keeps real
        // content of its own (never empty a page down to headings).
        let carry = []
        if (realCount >= 1) {
          while (
            pageItems.length > 0 &&
            (pageItems[pageItems.length - 1].isSectionHeading ||
              pageItems[pageItems.length - 1].isSub)
          ) {
            carry.unshift(pageItems.pop())
          }
        }
        flushPage(pageItems)
        pageItems = carry
        pageTop = carry.length > 0 ? carry[0].top : it.top
      }
      pageItems.push(it)
    }
    flushPage(pageItems)
    setPages(out)
  }, [visibleSections, contentW, contentH, q, measureTick])

  // ── View model: front cover (0) → content (1..N) → back cover (last) ───────
  // Recto-first: a two-page spread opens like a printed book — the FIRST page
  // sits on the RIGHT, facing a blank left page inside the front cover. A
  // single leading blank page delivers that; every pairing / turn index below
  // then works unchanged. Single-page mode has no left/right, so no blank.
  const book = twoUp ? [{ blank: true, segments: [] }, ...pages] : pages
  const realTotal = pages.length
  const total = book.length
  // A content "unit" is a spread (2 pages) in two-up, else a single page.
  const contentUnits =
    realTotal === 0 ? 0 : twoUp ? Math.ceil(total / 2) : total
  const lastView = contentUnits + 1
  const [viewIndex, setViewIndex] = useState(0)
  const [anim, setAnim] = useState(null) // { dir, from, to, nonce } during a turn
  const flipTimer = useRef(null)
  const nonceRef = useRef(0)

  const safeView = Math.min(Math.max(viewIndex, 0), Math.max(lastView, 0))
  const animActive =
    !!anim &&
    anim.from >= 0 &&
    anim.from <= lastView &&
    anim.to >= 0 &&
    anim.to <= lastView
  const displayView = animActive ? anim.to : safeView

  const isCoverView = (v) => v === 0 || v === lastView
  // First content page index backing a content view.
  const leftPageOfView = (v) => (twoUp ? (v - 1) * 2 : v - 1)

  // Start closed (front cover) on role change; clamp if pagination shrank.
  useEffect(() => {
    setViewIndex(0)
    setAnim(null)
  }, [manual])
  useEffect(() => {
    setViewIndex((v) => Math.min(v, contentUnits + 1))
  }, [contentUnits])
  useEffect(
    () => () => {
      if (flipTimer.current) clearTimeout(flipTimer.current)
    },
    [],
  )

  // Turn to `target` view. A turn in progress is finalised instantly first, so
  // rapid Next/Prev never leaves a half-turned leaf.
  const flipTo = (target, dir) => {
    const base = anim ? anim.to : viewIndex
    if (target < 0 || target > lastView || target === base) return
    if (flipTimer.current) clearTimeout(flipTimer.current)
    setViewIndex(base)
    nonceRef.current += 1
    setAnim({ dir, from: base, to: target, nonce: nonceRef.current })
    flipTimer.current = setTimeout(() => {
      setViewIndex(target)
      setAnim(null)
      flipTimer.current = null
    }, FLIP_MS)
  }
  const goNext = () => flipTo((anim ? anim.to : viewIndex) + 1, 'next')
  const goPrev = () => flipTo((anim ? anim.to : viewIndex) - 1, 'prev')

  // Arrow-key page turning.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
        return
      }
      if (e.key === 'ArrowRight') goNext()
      else if (e.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIndex, anim, lastView])

  // One A4 paper face — the section segment(s) packed onto it, each a heading
  // (only where a section starts on this page) followed by its blocks. Segments
  // are flattened to direct children of the body so the first-child margin reset
  // still lands on the real first element. `left` mirrors the skin for a
  // left-hand page (binding on the right).
  const renderPaper = (page, idx, left = false) => {
    if (!page) return null
    return (
    <div className={`um-paper${left ? ' um-paper--left' : ''}`}>
      <div className="um-paper__body">
        {page.blank
          ? null
          : (page.segments ?? [])
              .flatMap((seg, si) => [
                seg.title ? (
                  <h2 key={`h-${si}`} className={PAGE_HEADING_CLASS}>
                    {seg.title}
                  </h2>
                ) : null,
                ...seg.blocks.map((block, i) => (
                  <div key={`b-${si}-${i}`}>
                    <Block block={block} q={q} />
                  </div>
                )),
              ])
              .filter(Boolean)}
      </div>
      {page.blank ? null : (
        <span className="um-page__folio">{twoUp ? idx : idx + 1}</span>
      )}
    </div>
    )
  }

  // A cover panel (the closed book). Clicking the front cover opens it; the
  // back cover clicks back into the manual.
  const renderCover = (side) => (
    <div
      className={`um-cover um-cover--${side}`}
      role="button"
      tabIndex={0}
      onClick={side === 'front' ? goNext : goPrev}
    >
      <div className="um-cover__inner">
        <Logo size="none" className="um-cover__logo" />
      </div>
    </div>
  )

  // A settled content view (spread in two-up, single page otherwise).
  const renderContentView = (v) => {
    if (!twoUp) {
      return <div className="um-sheet">{renderPaper(book[v - 1], v - 1)}</div>
    }
    const rightX = pageW + SPREAD_GAP
    const L = leftPageOfView(v)
    return (
      <>
        <div className="um-slot" style={{ left: 0, width: pageW }}>
          {renderPaper(book[L], L, true)}
        </div>
        <div className="um-slot" style={{ left: rightX, width: pageW }}>
          {renderPaper(book[L + 1], L + 1, false)}
        </div>
      </>
    )
  }

  const renderView = (v) => {
    if (v === 0) return renderCover('front')
    if (v === lastView) return renderCover('back')
    return renderContentView(v)
  }

  // The book: closed covers, content spreads/pages, and the turns between them.
  const renderBook = () => {
    if (!animActive) return renderView(displayView)

    // Opening from / closing to a cover — a light open fade of the destination.
    if (isCoverView(anim.from) || isCoverView(anim.to)) {
      return (
        <div
          key={anim.nonce}
          className="um-open"
          style={{ position: 'absolute', inset: 0 }}
        >
          {renderView(anim.to)}
        </div>
      )
    }

    // ── Content → content: the physical page turn ──
    if (!twoUp) {
      const staticV = anim.dir === 'next' ? anim.to : anim.from
      const turningV = anim.dir === 'next' ? anim.from : anim.to
      return (
        <>
          <div className="um-sheet um-sheet--static">
            {renderPaper(book[staticV - 1], staticV - 1)}
          </div>
          <div key={anim.nonce} className={`um-sheet um-turn um-turn--${anim.dir}`}>
            <div className="um-face um-face--front">
              {renderPaper(book[turningV - 1], turningV - 1)}
            </div>
            <div className="um-face um-face--back" aria-hidden="true" />
          </div>
        </>
      )
    }
    const rightX = pageW + SPREAD_GAP
    const L = leftPageOfView(anim.from)
    if (anim.dir === 'next') {
      return (
        <>
          <div className="um-slot" style={{ left: 0, width: pageW }}>
            {renderPaper(book[L], L, true)}
          </div>
          <div className="um-slot" style={{ left: rightX, width: pageW }}>
            {renderPaper(book[L + 3], L + 3, false)}
          </div>
          <div
            key={anim.nonce}
            className="um-leaf um-leaf--next"
            style={{ left: rightX, width: pageW }}
          >
            <div className="um-face um-face--front">
              {renderPaper(book[L + 1], L + 1, false)}
            </div>
            <div className="um-face um-face--back">
              {renderPaper(book[L + 2], L + 2, true) || (
                <div className="um-paper um-paper--left" />
              )}
            </div>
          </div>
        </>
      )
    }
    return (
      <>
        <div className="um-slot" style={{ left: rightX, width: pageW }}>
          {renderPaper(book[L + 1], L + 1, false)}
        </div>
        <div className="um-slot" style={{ left: 0, width: pageW }}>
          {renderPaper(book[L - 2], L - 2, true)}
        </div>
        <div
          key={anim.nonce}
          className="um-leaf um-leaf--prev"
          style={{ left: 0, width: pageW }}
        >
          <div className="um-face um-face--front">
            {renderPaper(book[L], L, true)}
          </div>
          <div className="um-face um-face--back">
            {renderPaper(book[L - 1], L - 1, false) || <div className="um-paper" />}
          </div>
        </div>
      </>
    )
  }

  const onCover = isCoverView(displayView)
  // Closed covers are a single page wide; open content is a full spread.
  const bookW = twoUp && !onCover ? spreadW : pageW
  const prevDisabled = displayView === 0
  const nextDisabled = displayView === lastView
  // Real page numbers on the visible spread. In two-up the `book` index equals
  // the printed page number (the leading blank is index 0, so it contributes no
  // number); the left slot may be the blank (opening spread) or run past the
  // last page (a spread that ends on a left-hand page).
  const counterText = (() => {
    if (onCover) return displayView === 0 ? 'Cover' : 'Back cover'
    if (!twoUp) return `Page ${leftPageOfView(displayView) + 1} of ${realTotal}`
    const li = leftPageOfView(displayView) // left slot's book index / page number
    const ri = li + 1 // right slot
    const firstReal = li === 0 ? ri : li
    const secondReal = li === 0 ? null : ri <= realTotal ? ri : null
    return secondReal
      ? `Pages ${firstReal}–${secondReal} of ${realTotal}`
      : `Page ${firstReal} of ${realTotal}`
  })()

  return (
    <div>
      <style>{BOOK_CSS}</style>
      <div className="user-manual-noprint">
        <Breadcrumb current="User Manual" homePath={homePath} />

        {/* Header — matches the standard page header (big title + subtitle on
         *  the left, primary action on the right). */}
        <div className="flex items-start justify-between mb-1.5 gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display text-2xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5">
              <BookOpen
                size={20}
                className="shrink-0 text-[color:var(--color-accent-strong)]"
              />
              {manual.title}
            </h1>
            <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
              {manual.subtitle}
            </p>
          </div>
          {/* Restart the first-login guided tour. Hidden on the Super Admin
           *  console, where the tour is intentionally not mounted. */}
          {user?.role !== 'super_admin' ? (
            <button
              type="button"
              onClick={() => startProductTour()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Compass size={15} />
              <span className="hidden sm:inline">Take Product Tour</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => printManual(manual)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <Printer size={15} />
            <span className="hidden sm:inline">Print / Save as PDF</span>
          </button>
        </div>
      </div>

      {/* Content — a book: one section per page, turned with a 3D flip. The
       *  left "On this page" ToC was removed by request; the book is centred at
       *  a comfortable reading width and navigated with Previous / Next (or the
       *  ← / → arrow keys). */}
      <div ref={frameRef} className="mx-auto max-w-[420mm]">
        {/* Off-screen measurer — drives A4 pagination. Never visible. */}
        <div
          ref={measureRef}
          aria-hidden="true"
          className="um-measure"
          style={{
            position: 'absolute',
            left: '-99999px',
            top: 0,
            width: `${contentW}px`,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {/* No `position: relative` here: the packer reads each item's
           *  `offsetTop`, which must be measured against the absolute measurer
           *  (continuous across sections) so content can flow section-to-section.
           *  A positioned wrapper would reset offsetTop per section and break the
           *  cross-section page math. */}
          {visibleSections.map((s) => (
            <div data-msec={s.id} key={s.id}>
              <h2 data-mi="h" className={PAGE_HEADING_CLASS}>
                {s.title}
              </h2>
              {s.blocks.map((block, i) => (
                <div data-mi="b" data-bi={i} key={i}>
                  <Block block={block} q={q} />
                </div>
              ))}
            </div>
          ))}
        </div>

        {realTotal === 0 ? (
          <p className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 text-sm text-[color:var(--color-ink-muted)]">
            Preparing the manual…
          </p>
        ) : (
          <>
            {/* The book: a static destination sheet with the outgoing leaf
             *  physically turning over it (or, when idle, one sheet). The
             *  outer wrapper is sized to the SCALED page so the whole A4 sheet
             *  stays inside the viewport (fit-to-width); the sheet itself is
             *  laid out at full `pageW` and scaled down via `fit`. */}
            <div className="relative">
              <div
                className="mx-auto overflow-hidden"
                style={{ width: bookW * fit, height: pageH * fit }}
              >
                {/* CSS `zoom` (not transform: scale) so the sheet is LAID OUT
                 *  and painted at its true on-screen size — text and screenshots
                 *  rasterise crisply instead of being a scaled-down bitmap. */}
                <div
                  className="um-stage um-spread"
                  style={{
                    width: bookW,
                    height: pageH,
                    maxWidth: 'none',
                    marginInline: 0,
                    zoom: fit,
                  }}
                >
                  {renderBook()}
                </div>
              </div>
              {/* Big page-turn arrows flanking the book — spread only; in the
               *  single-page view the full-width page leaves no room for them
               *  (the bottom Previous / Next controls handle it there). */}
              {twoUp ? (
                <>
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={prevDisabled}
                    aria-label="Previous page"
                    className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 z-10 grid h-11 w-11 place-items-center rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] shadow-md hover:bg-[color:var(--color-card-hover)] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={nextDisabled}
                    aria-label="Next page"
                    className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 z-10 grid h-11 w-11 place-items-center rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] shadow-md hover:bg-[color:var(--color-card-hover)] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronRight size={22} />
                  </button>
                </>
              ) : null}
            </div>

            {/* Bottom bar — just the page counter in the spread (the flanking
             *  arrows navigate); the single-page view keeps Previous / Next
             *  here since it has no side arrows. */}
            <div
              className={`mx-auto mt-1.5 flex items-center gap-3 ${
                twoUp ? 'justify-center' : 'justify-between'
              }`}
              style={{ maxWidth: bookW * fit }}
            >
              {!twoUp ? (
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={prevDisabled}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronLeft size={15} />
                  <span className="hidden sm:inline">Previous</span>
                </button>
              ) : null}
              <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                {counterText}
              </span>
              {!twoUp ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={nextDisabled}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight size={15} />
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default UserManualPage
